import { Injectable, EventEmitter } from '@angular/core';
import { CapitalizePipe, FieldNamePipe } from '@app/common-feature';
import * as ext from '@app/extensions';
import { _tr } from '@app/extensions';
import { HttpService } from '../../services/http.service';
import { FieldWrapperModel } from '../../services/package/field/field.model';
import { FieldType } from '../../services/package/field/field-type.enum';
import { Info } from './package.model';
import { AppearanceType } from '../../services/appearance/appearance.enum';
import { overlayConfigFactory } from 'ngx-modialog-11';
import { Modal, BSModalContext } from 'ngx-modialog-11/plugins/bootstrap';
import { MessageBoxComponent } from '../../dialogs/message-box/message-box.component';
import { PdfDownloadComponent } from '../../dialogs/pdf-download/pdf-download.component';
import { environment } from '@env/environment';
import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class PackageService {
  package;

  environment;

  onChangeTextFieldValue = new EventEmitter();
  onClearFieldValue = new EventEmitter();

  private fields: FieldWrapperModel[] = [];

  private lastisRequiredField: FieldWrapperModel;

  private fieldsInfo: Info;

  constructor(
    private modal: Modal,
    private httpService: HttpService,
    private capitalizePipe: CapitalizePipe,
    private fieldNamePipe: FieldNamePipe,
  ) {
    this.environment = environment;
  }

  init(data, fieldsInfo: Info) {
    this.package = data;

    if (!this.package) {
      return;
    }

    this.fieldsInfo = fieldsInfo;

    this.wrappingFields(this.package.documents, fieldsInfo);
    this.setLastRequiredField();
  }

  get fieldsArr() {
    return this.fields;
  }

  // Package
  get isSignedPackage(): boolean {
    if (!this.package) {
      return true;
    }

    for (let i = 0; i < this.package.documents.length; i++) {
      if (!this.package.documents[i].roles) {
        continue;
      }

      for (let j = 0; j < this.package.documents[i].roles.length; j++) {
        if (!this.package.documents[i].roles[j].isComplete) {
          return false;
        }
      }
    }

    return true;
  }

  getPackageName(): String {
    if (!this.package) {
      return '';
    }

    return this.package.name;
  }

  get() {
    return this.package;
  }

  get documentCount() {
    if (!this.package) {
      return 0;
    }

    if (!this.package.documents) {
      return 0;
    }

    return this.package.documents.length;
  }

  download(args) {
    if (!this.package) {
      return;
    }

    //if (value === DownloadType.Enum.One) {

    //    this.downloadOneByOne(this.package.documents);
    //    return;
    //}

    if (this.documentCount > 1) {
      this.downloadPopupOpen(this.package.documents);
    } else if (this.documentCount === 1) {
      this.downloadOneDocument(this.package.documents[0]);
    }
    //this.httpService.downloadFile(`api/v1/packages/${this.package.id}/documents/zip`);
  }

  get recipient() {
    return this.package && this.package.recipients && this.package.recipients[0];
  }

  // Fields
  get isComplitedRequiredFields() {
    return this.fieldsInfo.required.isComplited;
  }

  renameFields() {
    if (!this.fields) {
      return;
    }

    for (let i = 0; i < this.fields.length; i++) {
      this.fields[i].rename();
    }
  }

  setFieldValue(field: FieldWrapperModel, value, callback?) {
    if (!field) {
      return;
    }
    const type = field.type;

    if (!type) {
      return;
    }

    if (type === FieldType.Enum.RadioButton) {
      this.setValueRadioButton(field);
    } else if (type === FieldType.Enum.CheckBox) {
      this.setValueCheckBox(field);
    } else if (type === FieldType.Enum.TextArea || type === FieldType.Enum.TextField) {
      field.setValue(value);

      setTimeout(() => {
        this.onChangeTextFieldValue.emit({
          field: field,
        });
      }, 100);
    } else {
      field.setValue(value);
    }

    this.setMissedRequiredFileds();

    if (type === FieldType.Enum.TextArea || type === FieldType.Enum.TextField) {
      return;
    }

    this.goToNextField(field, callback);
  }

  sign(field: FieldWrapperModel, callback) {
    if (!field) {
      return;
    }

    field.setValue(field.type === FieldType.Enum.Signature ? AppearanceType.Signature : AppearanceType.Initial);

    this.setValueAutoCompleteFields(field.id);
    this.setMissedRequiredFileds();

    const nextFieldData = this.getNextRequiredFieldData(field);

    if (callback) {
      callback(field, nextFieldData);
    }
  }

  isFirstRequiredField(field) {
    if (!this.fields || !field) {
      return false;
    }

    const id = field.id;
    const index = this.fields.map((x) => x.id).indexOf(id);

    if (index < 0) {
      return false;
    }

    if (index === 0) {
      return true;
    }

    for (let i = index - 1; i >= 0; i--) {
      if (this.fields[i].isRequired && !this.fields[i].isSigned) {
        return false;
      }
    }

    return true;
  }

  getFiledWrapper(fieldId): FieldWrapperModel {
    if (!fieldId) {
      return null;
    }

    for (let i = 0; i < this.fields.length; i++) {
      if (this.fields[i].id === fieldId) {
        return this.fields[i];
      }
    }

    return null;
  }

  getFileds() {
    return this.fields;
  }

  getFields(documentId) {
    const fields = [];

    if (!this.fields) {
      return null;
    }

    for (let i = 0; i < this.fields.length; i++) {
      if (this.fields[i].documentId === documentId) {
        fields.push(this.fields[i]);
      }
    }

    return fields;
  }

  getFirstRequiredField() {
    if (!this.fields) {
      return null;
    }

    for (let i = 0; i < this.fields.length; i++) {
      if (this.fields[i].isRequired && !this.fields[i].isSigned) {
        const index = this.package.documents.map((x) => x.id).indexOf(this.fields[i].documentId);

        return this.fields[i];
      }
    }

    return null;
  }

  getFirstRequiredFieldData() {
    if (!this.fields) {
      return null;
    }

    for (let i = 0; i < this.fields.length; i++) {
      if (this.fields[i].isRequired && !this.fields[i].isSigned) {
        const index = this.package.documents.map((x) => x.id).indexOf(this.fields[i].documentId);

        return {
          document: this.package.documents[index],
          field: this.fields[i].getField(), // TODO should be given field wrap
        };
      }
    }

    return null;
  }

  getNextRequiredFieldData(field: FieldWrapperModel) {
    if (!field) {
      return null;
    }

    if (this.lastisRequiredField && this.lastisRequiredField.id === field.id) {
      return null;
    }

    let index = this.fields.map((x) => x.id).indexOf(field.id);

    if (index < 0) {
      return null;
    }

    for (let i = index + 1; i < this.fields.length; i++) {
      if (this.fields[i] && this.fields[i].isRequired && !this.fields[i].isSigned) {
        index = this.package.documents.map((x) => x.id).indexOf(this.fields[i].documentId);

        return {
          document: this.package.documents[index],
          field: this.fields[i],
          isVisible: false,
        };
      }
    }
    return null;
  }

  getNextRequiredField(prevField: FieldWrapperModel): FieldWrapperModel {
    if (!prevField) {
      return;
    }

    if (this.lastisRequiredField && this.lastisRequiredField.id === prevField.id) {
      return null;
    }

    const index = this.fields.map((x) => x.id).indexOf(prevField.id);

    if (index < 0) {
      return null;
    }

    for (let i = index + 1; i < this.fields.length; i++) {
      if (this.fields[i].isRequired && !this.fields[i].isSigned) {
        return this.fields[i];
      }
    }

    return null;
  }

  getMissedRequiredFiledsInfo() {
    const info = {};

    if (!this.fields) {
      return false;
    }

    for (let i = 0; i < this.fields.length; i++) {
      if (this.fields[i].isMissed) {
        info[this.fields[i].documentName] = ++info[this.fields[i].documentName] || 1;
      }
    }

    return info;
  }

  get isLastRequiedFieldSigned() {
    if (!this.lastisRequiredField) {
      return false;
    }

    return this.lastisRequiredField.isSigned;
  }

  // Clear
  clearField(field) {
    if (!field) {
      return;
    }

    this.clear(field);
  }

  clearFields() {
    this.modal.open(
      MessageBoxComponent,
      overlayConfigFactory(
        {
          title: _tr('You are about to clear all fields you have filled in this envelope'),
          classPopup: 'vertical-btn',
          buttons: [
            {
              text: _tr('Clear fields'),
              callback: this.clearFieldsCallback,
            },
            {
              text: _tr('Cancel'),
              classes: 'empty-secondary-button',
            },
          ],
          image: 'fa-exclamation-triangle',
          svg: {
            xlinkHref: '#dialog-info-warning',
            xlinkHrefDisabled: '',
          },
          classes: 'warning',
        },
        BSModalContext,
      ),
    );
  }

  clareFiledByType(type) {
    if (!this.fields) {
      return;
    }

    for (let i = 0; i < this.fields.length; i++) {
      if (this.fields[i].isSigned && this.fields[i].type === type) {
        this.clear(this.fields[i]);
      }
    }
  }

  // Atachments
  isComplitedRequiredAtachments(atachments) {
    if (!atachments) {
      return false;
    }

    let result = false;

    for (let i = 0; i < atachments.length; i++) {
      if (atachments[i].isRequired) {
        result = true;

        if (atachments[i].attachmentStatus !== 'complete') {
          return false;
        }
      }
    }

    return result;
  }

  isComplitedOptionalAtachments(atachments) {
    if (!atachments) {
      return false;
    }

    let result = false;

    for (let i = 0; i < atachments.length; i++) {
      if (!atachments[i].isRequired) {
        result = true;

        if (atachments[i].attachmentStatus !== 'complete') {
          return false;
        }
      }
    }

    return result;
  }

  initAttachmentsInfo(attachments, info: Info) {
    if (!attachments || !info) {
      return;
    }

    info.hasItems = attachments.length > 0;

    info.required.count = 0;
    info.optional.count = 0;

    for (let i = 0; i < attachments.length; i++) {
      if (attachments[i].attachmentStatus === 'complete') {
        continue;
      }

      if (attachments[i].isRequired) {
        info.required.count++;
      } else {
        info.optional.count++;
      }
    }
  }

  hasRequiredAtachments(atachments) {
    if (!atachments) {
      return false;
    }

    for (let i = 0; i < atachments.length; i++) {
      if (atachments[i].isRequired) {
        return true;
      }
    }

    return false;
  }

  // Documents
  private downloadPopupOpen(documents) {
    if (!documents) {
      return;
    }

    this.modal.open(
      PdfDownloadComponent,
      overlayConfigFactory(
        {
          title: _tr('Download PDF files'),
          callback: (document) => {
            if (document === null) {
              this.downloadZip();
            } else if (document) {
              this.downloadOneDocument(document);
            }
          },
          model: {
            documents: documents,
          },
          buttons: [],
        },
        BSModalContext,
      ),
    );
  }

  private downloadOneByOne(documents) {
    if (!documents || !this.package) {
      return;
    }

    if (documents.length < 1) {
      return;
    }
    let i = 1;

    this.httpService.downloadFile(`api/v1/packages/${this.package.id}/documents/${documents[0].id}/download`);

    const id = setInterval(() => {
      // TODO should be moved to zone
      const doc = documents[i];

      if (!doc) {
        clearInterval(id);
        return;
      }

      i++;

      this.httpService.downloadFile(`api/v1/packages/${this.package.id}/documents/${doc.id}/download`);
    }, 2000);
  }

  private downloadOneDocument(docuemnt) {
    if (!document) {
      return;
    }

    this.httpService.downloadFile(`api/v1/packages/${this.package.id}/documents/${docuemnt.id}/download`);
  }

  private downloadZip() {
    if (!this.package) {
      return;
    }

    this.httpService.downloadFile(`api/v1/packages/${this.package.id}/documents/zip`);
  }

  // Fileds
  private goToNextField(field: FieldWrapperModel, callback) {
    //if (!field || !this.isSignStarted_) {
    //    return;
    //}
    if (!this.fields) {
      return;
    }
    const nextFieldData = this.getNextRequiredFieldData(field);

    if (callback) {
      callback(field, nextFieldData);
    }
  }

  private setValueAutoCompleteFields(id) {
    if (!this.fields || !this.package) {
      return;
    }
    const recipient = this.recipient;

    if (!recipient) {
      return;
    }
    for (let i = 0; i < this.fields.length; i++) {
      if (!this.fields[i].isAutoComplete) {
        continue;
      }
      if (this.fields[i].parentId != id) {
        continue;
      }
      this.setValueAutoCompleteField(this.fields[i], recipient);
    }
  }

  private setValueAutoCompleteField(field, recipient) {
    switch (field.type) {
      case 'date':
        let formatDate = this.package.signingDateFormat || 'M/d/yyyy';
        const locale = this.package.language;

        if (formatDate.includes('tt')) {
          formatDate = formatDate.replace('tt', 'a');
        }

        field.setValue(this.formatDateTime(formatDate, locale));
        break;
      case 'email':
        if (recipient.email) {
          field.setValue(recipient.email);
        }
        break;
      case 'name':
        if (recipient.firstName || recipient.lastName) {
          field.setValue(recipient.firstName + (recipient.lastName ? ' ' + recipient.lastName : ''));
        }
        break;
      case 'company':
        if (recipient.company) {
          field.setValue(recipient.company);
        }

        break;
      case 'title':
        if (recipient.title) {
          field.setValue(recipient.title);
        }

        break;
    }
  }

  private formatDateTime(format: string, locale: string): string {
    const currentDate = DateTime.local();
    return currentDate.toFormat(format, { locale });
  }

  private setValueCheckBox(field: FieldWrapperModel) {
    if (!field) {
      return;
    }
    const value = !field.value;

    field.setValue(value ? value : null);
  }

  private setValueRadioButton(field: FieldWrapperModel) {
    if (!field || !this.fields) {
      return;
    }

    field.setValue(true);

    const id = field.id;
    const radioGroup = field.getRadioGroup();

    for (let i = 0; i < this.fields.length; i++) {
      if (this.fields[i].id === id) {
        continue;
      }

      if (this.fields[i].type === FieldType.Enum.RadioButton && this.fields[i].getRadioGroup() === radioGroup) {
        this.fields[i].setValue(false);
      }
    }
  }

  private setMissedRequiredFileds() {
    if (!this.fields) {
      return;
    }

    if (this.lastisRequiredField && this.lastisRequiredField.isSigned) {
      for (let i = 0; i < this.fields.length; i++) {
        if (this.fields[i].isRequired && !this.fields[i].isSigned) {
          this.fields[i].setMissed();
        }
      }
    }

    if (this.fieldsInfo) {
      this.fieldsInfo.required.isMissed = this.checkMissedRequiredFileds();
    }
  }

  private checkMissedRequiredFileds() {
    if (!this.fields) {
      return false;
    }

    for (let i = 0; i < this.fields.length; i++) {
      if (this.fields[i].isMissed) {
        return true;
      }
    }

    return false;
  }

  private clear(field?: FieldWrapperModel) {
    if (field) {
      const type = field.type;

      if (type === FieldType.Enum.RadioButton) {
        this.clearRadioGroupValues(field);
      } else {
        field.setValue(undefined);

        if (type === FieldType.Enum.Signature || type === FieldType.Enum.Initials) {
          this.clearAutoCompleteFields(field.id);
        }
      }
    } else {
      this.clearFields_();
    }

    this.setMissedRequiredFileds();

    this.onClearFieldValue.emit({
      field: field,
    });
  }

  private clearAutoCompleteFields(id) {
    for (let i = 0; i < this.fields.length; i++) {
      if (!this.fields[i].isAutoComplete) {
        continue;
      }

      if (this.fields[i].parentId !== id) {
        continue;
      }

      this.fields[i].setValue(undefined);
      this.fields[i].setMissed(false);
    }
  }

  private clearFields_() {
    for (let i = 0; i < this.fields.length; i++) {
      this.fields[i].setValue(undefined);
      this.fields[i].setMissed(false);
    }
  }

  private clearFieldsCallback = () => {
    if (!this.fields) {
      return;
    }

    const data = {};

    for (let i = 0; i < this.package.documents.length; i++) {
      const document = this.package.documents[i];
      const documentRoles = {};

      data[document.id] = documentRoles;

      for (let j = 0; j < document.roles.length; j++) {
        const role = document.roles[j];

        if (role.isComplete) {
          continue;
        }

        const roleFields = {};

        documentRoles[role.id] = roleFields;

        const filledRadioGroups = {};

        for (let k = 0; k < role.fields.length; k++) {
          const field = role.fields[k];

          roleFields[field.id] = null;
        }
      }
    }

    return this.httpService.postData(`api/v1/packages/${this.package.id}/fields`, data).subscribe(
      (response) => {
        this.clear();
      },
      (error) => {
        this.modal.open(
          MessageBoxComponent,
          overlayConfigFactory(
            {
              description: _tr('Failed to clear fields'),
              buttons: [
                {
                  text: _tr('Ok'),
                },
              ],
            },
            BSModalContext,
          ),
        );
      },
    );
  };

  private clearRadioGroupValues(field: FieldWrapperModel) {
    if (!field || !this.fields) {
      return;
    }

    const radioGroup = field.getRadioGroup();

    if (ext.isUndefinedOrNull(radioGroup)) {
      return;
    }

    for (let i = 0; i < this.fields.length; i++) {
      if (this.fields[i].getRadioGroup() === radioGroup) {
        this.fields[i].setValue(null);
      }
    }
  }

  // Init
  private wrappingFields(documents, fieldsInfo: Info) {
    if (!documents) {
      return;
    }

    this.fields = [];

    for (let i = 0; i < documents.length; i++) {
      if (!documents[i].roles) {
        continue;
      }
      for (let j = 0; j < documents[i].roles.length; j++) {
        if (!documents[i].roles[j].fields) {
          continue;
        }
        const parentSignature: any = this.getFirstSignature(documents[i].roles[j].fields);

        for (let k = 0; k < documents[i].roles[j].fields.length; k++) {
          const field = documents[i].roles[j].fields[k];

          if (!field) {
            continue;
          }
          const info = field.isRequired ? fieldsInfo.required : fieldsInfo.optional;

          if (!field.isAutoComplete) {
            info.isEmpty = false;
          }

          if (field.isAutoComplete && !field.parentId) {
            field.parentId = parentSignature && parentSignature.id;
          }
          const fieldWrapModel = new FieldWrapperModel(documents[i], documents[i].roles[j], field, this.capitalizePipe, this.fieldNamePipe, info);

          this.fields.push(fieldWrapModel);
        }
      }
    }
    fieldsInfo.hasItems = this.fields.length > 0;
  }

  private getFirstSignature(fields) {
    const signatures = fields.filter((x) => x.fieldType === FieldType.Enum.Signature || x.fieldType === FieldType.Enum.Initials);

    return signatures && signatures.length > 0 && signatures[0];
  }

  private setLastRequiredField() {
    if (!this.fields) {
      return;
    }

    for (let i = this.fields.length - 1; i >= 0; i--) {
      if (this.fields[i].isRequired) {
        this.lastisRequiredField = this.fields[i];
        return;
      }
    }
  }
}
